import QuestionLabel from './QuestionLabel';
import React, { useEffect, useState } from 'react';
import { formatCurrency } from '../../../lib/CurrencyHelpers.js';
import { minutesToDuration } from '../../../lib/DateTime.js';
import { CheckIcon } from '@heroicons/react/24/outline';
import Stepper from '../../Stepper.jsx';
import SelectItemButton from './components/SelectItemButton.jsx';

const AddonButton = ({
    addon,
    onClick,
    quantity,
    onChangeQuantity,
    enableQuantity,
    isSelected,
    layout,
}) => {
    return (
        <SelectItemButton
            onClick={onClick}
            quantity={quantity}
            onChangeQuantity={onChangeQuantity}
            enableQuantity={enableQuantity}
            isSelected={isSelected}
            quantityIncrement={addon.quantityIncrement}
            layout={layout}
        >
            <div className="@sm:flex items-center justify-between">
                <div className="flex-1 mr-2">
                    <div className="font-semibold">{addon.name}</div>

                    {!!addon.description && (
                        <div className="mt-2 text-gray-500 text-sm">
                            {addon.description}
                        </div>
                    )}
                </div>
                <div className="text-gray-500 flex space-x-4 mr-4">
                    {addon.price > 0 && (
                        <div>
                            {formatCurrency(
                                addon.price * Math.max(1, quantity),
                            )}
                        </div>
                    )}
                    {addon.duration > 0 && (
                        <div>+{minutesToDuration(addon.duration)}</div>
                    )}
                </div>
            </div>
        </SelectItemButton>
    );
};

const AddonQuestion = ({ label, value, setValue, settings }) => {
    const addons = settings?.addons || [];

    useEffect(() => {
        // Update price!
        const currentPrice = value?.price;
        let newPrice = 0;
        for (const selection of value?.addons || []) {
            newPrice += selection.addon.price * selection.quantity;
        }

        if (currentPrice !== newPrice) {
            setValue({ ...value, price: newPrice });
        }
    }, [JSON.stringify(value)]);

    function toggleAddon(addon) {
        const idx = findAddon(addon);
        if (idx > -1) {
            // remove
            const newAddons = [...value.addons];
            newAddons.splice(idx, 1);
            setValue({ ...value, addons: newAddons });
        } else {
            // Add
            setValue({
                ...value,
                addons: [
                    ...(value?.addons || []),
                    {
                        addon: addon,
                        quantity: 1,
                    },
                ],
            });
        }
    }

    function setAddonQuantity(addon, quantity) {
        if (quantity <= 0) {
            // Just remove
            return toggleAddon(addon);
        }
        const idx = findAddon(addon);
        if (idx < 0) {
            return;
        }
        const newAddons = [...value.addons];
        newAddons[idx].quantity = quantity;
        setValue({ ...value, addons: newAddons });
    }

    function findAddon(addon) {
        for (const i in value?.addons || []) {
            //console.log('checking if', addon, 'selected compared to', value.addons[i].addon)
            if (
                JSON.stringify(value.addons[i].addon) === JSON.stringify(addon)
            ) {
                return i;
            }
        }

        return -1;
    }

    return (
        <label>
            <QuestionLabel label={label} settings={settings} />
            <div
                className={`@container ${settings.layout === 'grid' ? 'grid grid-cols-2 gap-4' : ''}`}
            >
                {addons.map((addon, idx) => {
                    const valueIdx = findAddon(addon);

                    return (
                        <AddonButton
                            key={idx}
                            addon={addon}
                            onClick={() => {
                                toggleAddon(addon);
                            }}
                            onChangeQuantity={newQuantity => {
                                setAddonQuantity(addon, newQuantity);
                            }}
                            quantity={
                                valueIdx > -1
                                    ? value?.addons[valueIdx]?.quantity
                                    : 0
                            }
                            isSelected={valueIdx > -1}
                            layout={settings?.layout}
                            enableQuantity={!!settings?.enableQuantity}
                        />
                    );
                })}
            </div>
        </label>
    );
};

export default AddonQuestion;
